import React, { useState } from 'react';
import { Layout } from '../components/Layout';
import { Plus, Search, AlertCircle, Trash2, Edit2 } from 'lucide-react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd'; 
import { useDrag } from '@use-gesture/react';
import { useSpring, animated } from '@react-spring/web';
import { QuickAdd } from '../components/Tasks/QuickAdd';
import { DailyTaskView } from '../components/Tasks/DailyTaskView';
import { CustomRoleManager } from '../components/Tasks/CustomRoleManager';
import { useTaskContext } from '../contexts/TaskContext';
import { useSubscription } from '../contexts/SubscriptionContext';
import { useNavigate } from 'react-router-dom';
import type { Role, Task, TaskStatus } from '../types';

export function TaskManager() {
  const navigate = useNavigate();
  const [editingTask, setEditingTask] = useState<Task | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const { tasks, loading, error, addTask, updateTask, deleteTask, changeTaskStatus } = useTaskContext();
  const { hasFeature } = useSubscription();
  const [activeRole, setActiveRole] = useState<Role>('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showCustomRoles, setShowCustomRoles] = useState(false);
  const [viewMode, setViewMode] = useState<'board' | 'daily'>('board');
  const isPremiumDaily = hasFeature('daily_task_view');
  const isEmbedded = /wv|WebView/.test(navigator.userAgent);

  const handleDeleteTask = async (taskId: string) => {
    try {
      const shouldDelete = isEmbedded || window.confirm('Are you sure you want to delete this task? This action cannot be undone.');
      if (shouldDelete) {
        await deleteTask(taskId);
      }
    } catch (err) {
      console.error('Error deleting task:', err);
      alert('Failed to delete task. Please try again.');
    }
  };

  const filteredTasks = tasks.filter(task => {
    const matchesRole = activeRole === 'all' ? true : task.role === activeRole;
    const matchesSearch = task.title.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesRole && matchesSearch;
  });

  const handleDragEnd = async (result: any) => {
    const { source, destination, draggableId } = result;
    
    if (!destination || 
        (source.droppableId === destination.droppableId && 
         source.index === destination.index)) {
      return;
    }
    
    try {
      await changeTaskStatus(draggableId, destination.droppableId as TaskStatus);
    } catch (err) {
      console.error('Error updating task status:', err);
    }
  };

  return (
    <Layout>
      <div className="space-y-8">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold mb-1">Tasks</h1>
            {error && (
              <div className="flex items-center gap-2 text-sm text-red-500">
                <AlertCircle className="w-4 h-4" />
                <p>Connection error. Please try again later.</p>
              </div>
            )}
          </div>
        </div>

        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder="Search tasks..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-1/2 pl-10 pr-4 py-1.5 bg-black border-b border-white/50 text-sm text-white placeholder-white focus:border-white transition-colors bg-gradient-to-r from-black via-black to-transparent"
          />
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-2 border-[var(--accent-color)] border-t-transparent mx-auto mb-4"></div>
            <p className="text-gray-400">Loading tasks...</p>
          </div>
        ) : (
          viewMode === 'daily' ? (
            isPremiumDaily ? (
              <DailyTaskView
                tasks={filteredTasks}
                onAddTask={() => setShowAddModal(true)}
                onEditTask={setEditingTask}
                onTaskMove={async (taskId, hour) => {
                  const task = tasks.find(t => t.id === taskId);
                  if (!task) return;
                  
                  const newDate = new Date(task.duedate || new Date());
                  newDate.setHours(hour);
                  
                  try {
                    await updateTask(taskId, { duedate: newDate.toISOString() });
                  } catch (err) {
                    console.error('Error updating task time:', err);
                  }
                }}
              />
            ) : (
              <div className="text-center py-12">
                <h2 className="text-xl font-bold mb-4">Premium Feature</h2>
                <p className="text-gray-400 mb-6">
                  Upgrade to Premium to access the Daily Task View feature.
                </p>
                <button
                  onClick={() => navigate('/subscription')}
                  className="px-6 py-3 bg-[var(--accent-color)] text-black rounded-xl font-medium hover:opacity-90 transition-opacity"
                >
                  Upgrade Now
                </button>
              </div>
            )
          ) : (
            <DragDropContext onDragEnd={handleDragEnd}>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {['todo', 'in-progress', 'done'].map((status) => (
                  <div key={status} className="space-y-4">
                    <div className="flex items-center justify-between">
                      <h2 className="font-semibold text-xl capitalize">
                        {status === 'in-progress' ? 'In Progress' : status} 
                      </h2>
                      <div className="flex items-center gap-2 md:gap-4">
                        <span className="text-sm text-gray-400">
                          {filteredTasks.filter(task => task.status === status).length}
                        </span>
                        {status === 'done' && filteredTasks.filter(task => task.status === 'done').length > 0 && (
                          <button
                            onClick={async () => {
                              const doneTasks = filteredTasks.filter(task => task.status === 'done');
                              if (confirm(`Archive ${doneTasks.length} completed tasks?`)) {
                                try {
                                  await Promise.all(
                                    doneTasks.map(task => 
                                      deleteTask(task.id)
                                    )
                                  );
                                } catch (err) {
                                  console.error('Error archiving tasks:', err);
                                }
                              }
                            }}
                            className="px-3 py-1.5 text-sm text-gray-400 hover:text-[var(--accent-color)] transition-colors bg-black/20 rounded-lg hover:bg-black/30 whitespace-nowrap"
                          >
                            Archive All
                          </button>
                        )}
                      </div>
                    </div>
                    <Droppable droppableId={status}>
                      {(provided) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.droppableProps}
                          className="space-y-2"
                        >
                          {filteredTasks
                            .filter(task => task.status === status)
                            .map((task, index) => (
                              <Draggable
                                key={task.id}
                                draggableId={task.id}
                                index={index}
                              >
                                {(provided) => (
                                  <animated.div
                                    ref={provided.innerRef}
                                    {...provided.draggableProps}
                                    {...provided.dragHandleProps}
                                    onTouchStart={(e) => {
                                      // Prevent drag and drop from interfering with swipe
                                      if (e.touches.length === 1) {
                                        provided.dragHandleProps?.onTouchStart?.(e as any);
                                      }
                                    }}
                                    onTouchMove={(e) => {
                                      if (e.touches.length === 1) {
                                        provided.dragHandleProps?.onTouchMove?.(e as any);
                                      }
                                    }}
                                    onTouchEnd={(e) => {
                                      if (e.changedTouches.length === 1) {
                                        provided.dragHandleProps?.onTouchEnd?.(e as any);
                                      }
                                    }}
                                    className={`p-4 rounded-xl ${
                                      status === 'todo' ? 'bg-[#141414]' :
                                      status === 'in-progress' ? 'bg-[#8d54c6]' :
                                      'bg-[#279686]'
                                    } relative hover:ring-1 hover:ring-white/10 transition-all duration-300`}
                                  >
                                    <div className="flex justify-between items-start mb-2">
                                      <h3 className="font-medium text-[15px] flex-1">{task.title}</h3>
                                      <div className="flex items-center gap-2">
                                        <button
                                          onClick={(e) => {
                                            handleDeleteTask(task.id);
                                          }}
                                          className="p-2 hover:bg-black/20 rounded-lg transition-colors text-red-500"
                                        >
                                          <Trash2 className="w-4 h-4" />
                                        </button>
                                      </div>
                                    </div>
                                    {task.description && (
                                      <p className="text-sm text-gray-400 mb-2">{task.description}</p>
                                    )}
                                    <div className="flex justify-between items-center">
                                      <div className="flex items-center gap-2">
                                        <span className={`text-xs px-2 py-1 rounded-full ${
                                        task.priority === 'high' ? 'bg-red-500/20 text-red-500' :
                                        task.priority === 'medium' ? 'bg-yellow-500/20 text-yellow-500' :
                                        'bg-green-500/20 text-green-500'
                                        }`}>
                                          {task.priority}
                                        </span>
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setEditingTask(task);
                                            setShowAddModal(true);
                                          }}
                                          className="p-1 hover:bg-black/20 rounded-lg transition-colors"
                                        >
                                          <Edit2 className="w-4 h-4" />
                                        </button>
                                      </div>
                                    </div>
                                  </animated.div>
                                )}
                              </Draggable>
                            ))}
                          {provided.placeholder}
                        </div>
                      )}
                    </Droppable>
                  </div>
                ))}
              </div>
            </DragDropContext>
          )
        )}
      </div>

      {/* Quick Add Modal */}
      {showAddModal && (
        <QuickAdd
          initialTask={editingTask}
          onAdd={async (task) => {
            try {
              if (editingTask) {
                await updateTask(editingTask.id, task);
              } else {
                await addTask(task);
              }
              setShowAddModal(false);
              setEditingTask(null);
            } catch (error) {
              console.error('Error saving task:', error);
            }
          }}
          onDelete={async (taskId) => {
            try {
              await deleteTask(taskId);
              setShowAddModal(false);
              setEditingTask(null);
            } catch (err) {
              console.error('Error deleting task:', err);
            }
          }}
          onClose={() => {
            setShowAddModal(false);
            setEditingTask(null);
          }}
        />
      )}

      {/* Quick Add Task Button */}
      <button
        onClick={() => setShowAddModal(true)}
        className="fixed bottom-24 right-6 md:bottom-8 w-14 h-14 bg-[var(--accent-color)] rounded-full shadow-lg flex items-center justify-center hover:opacity-90 transition-opacity z-40"
      >
        <Plus className="w-6 h-6 text-black" />
      </button>

      {showCustomRoles && (
        <CustomRoleManager onClose={() => setShowCustomRoles(false)} />
      )}
    </Layout>
  );
}
